import { useState } from 'react';
import DualDate from '../../components/common/DualDate';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import BeltBadge from '../../components/common/BeltBadge';
import { getBeltExams, getExamDetail, gradeStudent } from '../../services/beltExamService';
import { MdEmojiEvents, MdClose, MdArrowBack, MdPeople, MdCheckCircle } from 'react-icons/md';
import { useCalendar } from '../../context/CalendarContext';

const STATUS_BADGE = {
  scheduled:   'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  completed:   'bg-green-100 text-green-700',
  cancelled:   'bg-red-100 text-red-600',
};

const STATUS_LABEL = {
  scheduled:   'Scheduled',
  in_progress: 'In Progress',
  completed:   'Completed',
  cancelled:   'Cancelled',
};

const RESULT_STYLE = {
  promoted: 'bg-green-100 text-green-700',
  failed:   'bg-red-100 text-red-600',
  held:     'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  pending:  'bg-nav-hover text-muted',
};

const SCORE_CATEGORIES = ['poomsae', 'sparring', 'boardBreaking', 'theory', 'attitude'];
const SCORE_LABELS = {
  poomsae:       'Poomsae / Forms',
  sparring:      'Sparring',
  boardBreaking: 'Board Breaking',
  theory:        'Theory / Knowledge',
  attitude:      'Attitude / Spirit',
};

function StudentAvatar({ student, size = 'md' }) {
  const dim = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';
  const name = student?.fullName || student?.user?.name || '';
  const initials = name ? name[0].toUpperCase() : '?';
  return (
    <div className={`${dim} bg-primary-light text-primary rounded-full flex items-center justify-center font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
}

// ── Grade Student Modal ───────────────────────────────────────────────────────
function GradeModal({ examId, participant, onClose, onSaved }) {
  const studentName = participant?.student?.fullName
    || participant?.student?.user?.name
    || 'Unknown Student';
  const memberId    = participant?.student?.platformStudentId || participant?.student?.studentId || '';
  const currentGrade = participant?.currentGrade || '—';
  const targetGrade  = participant?.targetGrade  || '—';

  const [scores, setScores] = useState(
    SCORE_CATEGORIES.reduce((acc, cat) => {
      acc[cat] = participant?.scores?.[cat] != null ? String(participant.scores[cat]) : '';
      return acc;
    }, {})
  );
  const [selectedResult, setSelectedResult] = useState(
    participant?.result && participant.result !== 'pending' ? participant.result : null
  );
  const [notes, setNotes] = useState(participant?.examinerNotes || '');
  const [error, setError]  = useState('');

  const allFilled = SCORE_CATEGORIES.every(
    cat => scores[cat] !== '' && !isNaN(Number(scores[cat]))
  );

  const overall = allFilled
    ? parseFloat((SCORE_CATEGORIES.reduce((s, cat) => s + Number(scores[cat]), 0) / SCORE_CATEGORIES.length).toFixed(2))
    : null;

  const suggestedResult = overall === null ? null
    : overall >= 7 ? 'promoted'
    : overall >= 5 ? 'held'
    : 'failed';

  const canSave = allFilled && selectedResult !== null;

  const mutation = useMutation({
    mutationFn: () => gradeStudent(examId, {
      studentId:     participant.student._id,
      scores:        Object.fromEntries(SCORE_CATEGORIES.map(cat => [cat, parseFloat(scores[cat])])),
      examinerNotes: notes,
      result:        selectedResult,
    }),
    onSuccess: () => { toast.success('Grades saved'); onSaved(); },
    onError:   (err) => setError(err.response?.data?.message || 'Failed to save grades'),
  });

  const resultOptions = [
    { value: 'promoted', label: 'Promoted', bg: 'bg-green-500', ring: 'ring-green-400', text: 'text-white', icon: '✅' },
    { value: 'held',     label: 'Held',     bg: 'bg-amber-400', ring: 'ring-amber-300', text: 'text-white', icon: '⏸' },
    { value: 'failed',   label: 'Failed',   bg: 'bg-red-500',   ring: 'ring-red-400',   text: 'text-white', icon: '❌' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <StudentAvatar student={participant?.student} />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-secondary text-sm leading-tight">{studentName}</p>
            {memberId && <p className="text-xs text-gray-400">{memberId}</p>}
            <div className="flex items-center gap-1.5 mt-1">
              <BeltBadge belt={currentGrade} size="xs" />
              <span className="text-gray-300 text-xs">→</span>
              <BeltBadge belt={targetGrade} size="xs" />
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 flex-shrink-0">
            <MdClose className="text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {error && <div className="bg-red-50 text-red-600 text-xs rounded-xl px-3 py-2">{error}</div>}

          {/* Score inputs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Scores</p>
              <p className="text-xs text-gray-400">Pass threshold: <span className="font-semibold text-gray-600">6 / 10</span></p>
            </div>
            <div className="space-y-2">
              {SCORE_CATEGORIES.map(cat => {
                const v = scores[cat];
                const num = parseFloat(v);
                const filled = v !== '' && !isNaN(num);
                const passing = filled && num >= 6;
                return (
                  <div key={cat} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 flex-1">{SCORE_LABELS[cat]}</span>
                    <div className="flex items-center gap-2">
                      <input
                        type="number" min="0" max="10" step="0.5"
                        value={v}
                        onChange={e => setScores(prev => ({ ...prev, [cat]: e.target.value }))}
                        placeholder="0–10"
                        className={`w-16 border rounded-lg px-2 py-1.5 text-sm text-center focus:outline-none focus:ring-2 transition-colors ${
                          filled
                            ? passing
                              ? 'border-green-300 bg-green-50 text-green-800 focus:ring-green-200'
                              : 'border-red-300 bg-red-50 text-red-800 focus:ring-red-200'
                            : 'border-gray-200 focus:ring-primary/30'
                        }`}
                      />
                      <span className={`text-xs font-medium w-10 ${!filled ? 'text-gray-300' : passing ? 'text-green-600' : 'text-red-500'}`}>
                        {!filled ? '—' : passing ? 'Pass' : 'Fail'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Overall score */}
          {overall !== null && (
            <div className={`rounded-xl px-4 py-3 ${overall >= 6 ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Overall Score</span>
                <span className={`text-xl font-bold ${overall >= 6 ? 'text-green-700' : 'text-red-600'}`}>
                  {overall} <span className="text-sm font-normal text-gray-400">/ 10</span>
                </span>
              </div>
              {suggestedResult && (
                <p className="text-xs text-gray-500 mt-1">
                  Suggested: <span className={`font-semibold capitalize ${
                    suggestedResult === 'promoted' ? 'text-green-700' :
                    suggestedResult === 'held'     ? 'text-amber-700' : 'text-red-600'
                  }`}>{suggestedResult}</span>
                </p>
              )}
            </div>
          )}

          {/* Result selector */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Decision *</p>
            <div className="flex gap-2">
              {resultOptions.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setSelectedResult(opt.value)}
                  className={`flex-1 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all ${
                    selectedResult === opt.value
                      ? `${opt.bg} ${opt.text} border-transparent ring-2 ${opt.ring} ring-offset-1`
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="block text-base mb-0.5">{opt.icon}</span>
                  {opt.label}
                  {suggestedResult === opt.value && selectedResult !== opt.value && (
                    <span className="block text-[9px] text-gray-400 mt-0.5">Suggested</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Private notes about this student's performance…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-100 flex-shrink-0">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSave || mutation.isPending}
            loading={mutation.isPending}
          >
            Save Scores
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Exam Detail View ──────────────────────────────────────────────────────────
function ExamDetail({ examId, onBack }) {
  const { formatDate } = useCalendar();
  const [gradingParticipant, setGradingParticipant] = useState(null);

  const { data: exam, isPending, refetch } = useQuery({
    queryKey: ['belt-exam-detail-trainer', examId],
    queryFn:  () => getExamDetail(examId).then(r => r.data.exam),
  });

  if (isPending) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-32 bg-gray-100 animate-pulse rounded-xl" />
        <div className="h-40 bg-gray-100 animate-pulse rounded-2xl" />
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="text-center py-16 text-gray-400">
        <p>Exam not found.</p>
        <Button variant="ghost" onClick={onBack} className="mt-4">Back</Button>
      </div>
    );
  }

  const pendingCount = exam.participants.filter(p => p.result === 'pending').length;
  const getStudentId = (p) => p.student?._id || p.student;

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-secondary transition-colors mt-0.5"
        >
          <MdArrowBack /> Back
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-bold text-secondary">{exam.title}</h1>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_BADGE[exam.status]}`}>
              {STATUS_LABEL[exam.status]}
            </span>
          </div>
          <p className="text-sm text-gray-400 mt-0.5">
            <DualDate date={exam.date} />{exam.venue ? ` · ${exam.venue}` : ''}
          </p>
        </div>
      </div>

      {exam.status === 'in_progress' && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total',   value: exam.participants.length,                                    bg: 'bg-gray-50',  text: 'text-secondary' },
            { label: 'Graded',  value: exam.participants.filter(p => p.result !== 'pending').length, bg: 'bg-blue-50',  text: 'text-blue-700'  },
            { label: 'Pending', value: pendingCount,                                                bg: 'bg-amber-50', text: 'text-amber-700' },
          ].map(({ label, value, bg, text }) => (
            <Card key={label} className={`${bg} text-center py-3`}>
              <p className={`text-2xl font-bold ${text}`}>{value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{label}</p>
            </Card>
          ))}
        </div>
      )}

      <Card className="p-0 overflow-hidden">
        {exam.participants.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-center text-gray-400">
            <MdPeople className="text-4xl text-gray-200 mb-2" />
            <p className="text-sm">No participants in this exam.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs text-gray-400 font-medium">
                  <th className="text-left px-4 py-3">Student</th>
                  <th className="text-left px-3 py-3 hidden sm:table-cell">Current</th>
                  <th className="text-left px-3 py-3 hidden sm:table-cell">Target</th>
                  {exam.status !== 'scheduled' && (
                    <>
                      <th className="text-center px-2 py-3 hidden lg:table-cell">Forms</th>
                      <th className="text-center px-2 py-3 hidden lg:table-cell">Spar</th>
                      <th className="text-center px-2 py-3 hidden lg:table-cell">Board</th>
                      <th className="text-center px-2 py-3 hidden lg:table-cell">Theory</th>
                      <th className="text-center px-2 py-3 hidden lg:table-cell">Attitude</th>
                      <th className="text-center px-3 py-3">Score</th>
                    </>
                  )}
                  <th className="text-left px-3 py-3">Result</th>
                  {exam.status === 'in_progress' && <th className="text-right px-4 py-3">Action</th>}
                </tr>
              </thead>
              <tbody>
                {exam.participants.map((p) => {
                  const name     = p.student?.fullName || p.student?.user?.name || '—';
                  const memberId = p.student?.platformStudentId || p.student?.studentId || '';
                  return (
                    <tr key={p._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <StudentAvatar student={p.student} size="sm" />
                          <div className="min-w-0">
                            <p className="font-medium text-secondary truncate">{name}</p>
                            {memberId && <p className="text-xs text-gray-400">{memberId}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 hidden sm:table-cell">
                        <BeltBadge belt={p.currentGrade} size="xs" />
                      </td>
                      <td className="px-3 py-3 hidden sm:table-cell">
                        <BeltBadge belt={p.targetGrade || exam.targetBeltLevel} size="xs" />
                      </td>
                      {exam.status !== 'scheduled' && (
                        <>
                          {SCORE_CATEGORIES.map(cat => (
                            <td key={cat} className="px-2 py-3 text-center hidden lg:table-cell">
                              {p.scores?.[cat] != null
                                ? <span className={p.scores[cat] >= 6 ? 'text-green-700 font-medium' : 'text-red-600 font-medium'}>{p.scores[cat]}</span>
                                : <span className="text-gray-300">—</span>
                              }
                            </td>
                          ))}
                          <td className="px-3 py-3 text-center">
                            {p.overallScore != null
                              ? <span className={`font-bold ${p.overallScore >= 6 ? 'text-green-700' : 'text-red-600'}`}>{p.overallScore}</span>
                              : <span className="text-gray-300">—</span>
                            }
                          </td>
                        </>
                      )}
                      <td className="px-3 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${RESULT_STYLE[p.result]}`}>
                          {p.result}
                        </span>
                      </td>
                      {exam.status === 'in_progress' && (
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setGradingParticipant(p)}
                            className="text-xs text-primary hover:underline font-medium"
                          >
                            {p.result === 'pending' ? 'Grade' : 'Edit'}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {exam.status === 'in_progress' && pendingCount > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-amber-50 text-xs text-amber-700 font-medium">
            {pendingCount} student{pendingCount !== 1 ? 's' : ''} still pending grading.
          </div>
        )}
        {exam.status === 'in_progress' && pendingCount === 0 && exam.participants.length > 0 && (
          <div className="px-4 py-3 border-t border-gray-100 bg-green-50 text-xs text-green-700 font-medium flex items-center gap-2">
            <MdCheckCircle /> All students graded. Ask your admin to finalize the exam.
          </div>
        )}
        {exam.status === 'completed' && (
          <div className="px-4 py-3 border-t border-gray-100 bg-green-50 text-xs text-green-700 font-medium flex items-center gap-2">
            <MdCheckCircle /> Exam finalized. Grades are locked.
          </div>
        )}
      </Card>

      {gradingParticipant && (
        <GradeModal
          examId={exam._id}
          participant={gradingParticipant}
          onClose={() => setGradingParticipant(null)}
          onSaved={() => {
            setGradingParticipant(null);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
function TrainerBeltExams() {
  document.title = 'Belt Exams — TrainAxis';
  const { formatDate } = useCalendar();
  const [statusFilter, setFilter] = useState('');
  const [activeExamId, setActiveExamId] = useState(null);

  const { data, isPending } = useQuery({
    queryKey: ['belt-exams-trainer', statusFilter],
    queryFn:  () => getBeltExams({ status: statusFilter }).then(r => r.data),
    enabled:  !activeExamId,
  });

  if (activeExamId) {
    return <ExamDetail examId={activeExamId} onBack={() => setActiveExamId(null)} />;
  }

  const exams = data?.exams || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex flex-wrap gap-2">
          {[
            ['',            'All'],
            ['scheduled',   'Scheduled'],
            ['in_progress', 'In Progress'],
            ['completed',   'Completed'],
          ].map(([val, label]) => (
            <button
              key={val}
              onClick={() => setFilter(val)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                statusFilter === val
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-primary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {isPending ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-40 bg-gray-100 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : !exams.length ? (
        <Card>
          <div className="flex flex-col items-center py-12 text-center">
            <MdEmojiEvents className="text-5xl text-gray-200 mb-3" />
            <p className="text-gray-400 text-sm">No belt exams found</p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {exams.map(exam => (
            <Card
              key={exam._id}
              className="flex flex-col gap-3 cursor-pointer transition-colors duration-150 hover:border-secondary"
              onClick={() => setActiveExamId(exam._id)}
            >
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <p className="font-semibold text-secondary text-sm leading-tight truncate">{exam.title}</p>
                  <p className="text-xs text-gray-400 mt-1"><DualDate date={exam.date} /></p>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0 ml-2 ${STATUS_BADGE[exam.status]}`}>
                  {STATUS_LABEL[exam.status]}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <BeltBadge belt={exam.targetBeltLevel} size="xs" />
                <span className="text-xs text-gray-400">{exam.participants.length} participant{exam.participants.length !== 1 ? 's' : ''}</span>
              </div>
              {exam.venue && <p className="text-xs text-gray-400">Venue: {exam.venue}</p>}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default TrainerBeltExams;
